import type { CmmdProjectLeadEvents } from "@cmmd-forge/contracts";

export function AgentEventHistory(props: {
  readonly events: CmmdProjectLeadEvents["events"];
  readonly loading: boolean;
  readonly error: string | null;
  readonly framed?: boolean;
}) {
  return (
    <section className={props.framed === false ? "" : "border-t py-4"}>
      <h4 className="text-sm font-medium">Recent activity</h4>
      {props.loading ? (
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          Loading durable activity…
        </p>
      ) : props.error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          Activity unavailable: {props.error}
        </p>
      ) : props.events.length ? (
        <ol className="mt-3 divide-y divide-border border-y border-border">
          {latestAgentEvents(props.events).map((event) => (
            <li key={event.id} className="py-3 text-sm">
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate">{event.eventType}</span>
                <time
                  className="shrink-0 text-xs text-muted-foreground"
                  dateTime={event.occurredAt}
                >
                  {new Date(event.occurredAt).toLocaleString()}
                </time>
              </span>
              <span className="mt-1 block truncate font-mono text-[11px] text-muted-foreground">
                {event.sourceLane}
                {event.correlationKey ? ` · correlation ${event.correlationKey}` : ""}
                {event.runId ? ` · run ${event.runId}` : ""}
                {event.threadId ? ` · thread ${event.threadId}` : ""}
                {event.attemptId ? ` · attempt ${event.attemptId}` : ""}
                {event.runtimeId ? ` · runtime ${event.runtimeId}` : ""}
              </span>
              {Object.keys(event.payload).length ? (
                <span className="mt-1 block break-words text-xs text-muted-foreground">
                  {eventPayloadSummary(event.payload)}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">No recent activity.</p>
      )}
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Latest 200 canonical project events. Older activity may not appear.
      </p>
    </section>
  );
}

export function latestAgentEvents(
  events: CmmdProjectLeadEvents["events"],
): CmmdProjectLeadEvents["events"] {
  return events.slice(-20).toReversed();
}

function eventPayloadSummary(payload: Readonly<Record<string, unknown>>): string {
  const summary = JSON.stringify(payload);
  return summary.length > 240 ? `${summary.slice(0, 237)}…` : summary;
}
